import RouteService from "@/services/RouteService";
import LlTooltip from "@/components/LlTooltip.vue";
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlSelect from '@/components/LlSelect.vue';
import ClientMatterSearch from '@/components/ClientMatterSearch.vue';
import useClientMatterStore from '@/services/useClientMatterStore';
import { getOrConnectClient, getOrConnectMatter } from '@/services/accounting/accountingConnectService';
import { parseCurrencyInput, formatCurrencyInputDisplay, formatCurrencyInputTyping } from '@/services/utils';
import { reloadParentOnly } from '@/utils/routeHelpers';
export default {
  name: "TrustDeposit",
  components: {
    LlTooltip,
    LlDatePicker,
    LlSecondaryButton,
    LlSelect,
    ClientMatterSearch
  },
  props: {
    stateParams: {
      type: Object,
      required: true
    },
    state: {
      type: Object,
      required: true
    },
    // Supplied by iocContainer for the route-mounted case; absent when a parent Vue
    // component mounts this inline, which is why every use of it is guarded.
    rootScope: {
      type: Object,
      required: false,
      default: null
    },
    onSaved: {
      type: Function,
      required: false,
      default: null
    },
    onCancel: {
      type: Function,
      required: false,
      default: null
    },
    /**
     * SC-45762 — set to true when opening from the Settlement tab.
     * Hides the client/matter dropdowns (matter is already known from the
     * settlement context) and pre-fills description with "Settlement Funds".
     * CLEANUP: remove this prop and the guard block in getTrust() once the
     * settlement wizard no longer uses TrustDeposit inline.
     */
    settlementContext: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      me: undefined,
      // What <ClientMatterSearch> reads and writes: lean option records (id/label only).
      // form.client / form.matter hold the full records the rest of this component needs,
      // enriched from these by the watchers below.
      cm: {
        client: null,
        matter: null
      },
      cmQuery: null,
      form: {
        date: new Date()
      },
      displayAmount: '',
      attrs: [{
        key: "today",
        highlight: {
          color: "purple",
          fillMode: "outline"
        },
        dates: new Date()
      }],
      memo: "",
      pick: false,
      loading: true,
      message: null,
      saving: false,
      accountingMethods: [],
      masks: {
        input: "MM-DD-YYYY"
      },
      hasDateError: false
    };
  },
  async mounted() {
    this.getTrust();
    this.getAccountingMethods();
  },
  watch: {
    // The picker only ever hands us lean option records, so swap in the full one whenever the
    // selection changes. Watching the id rather than the object keeps <ClientMatterSearch>'s own
    // re-assignment of the same selection from triggering a redundant fetch.
    'cm.client.id': function watchClientId(id) {
      this.syncClient(id);
    },
    'cm.matter.id': function watchMatterId(id) {
      this.syncMatter(id);
    }
  },
  computed: {
    // Exposed as a computed rather than resolved inline so it stays stubbable the way the rest
    // of this component's collaborators are.
    clientMatterStore() {
      return useClientMatterStore();
    },
    firstDeposit() {
      const {
        client,
        matter
      } = this.form;
      if (!client) {
        return false;
      }
      if (client.billing?.billClient) {
        return !client.billing.trust;
      }
      return !!matter && !matter.billing?.trust;
    },
    trustAccountNameExceeds100() {
      const label = this.form.client?.billing?.billClient ? this.form.client?.label : this.form.matter?.label;
      return label?.length > 100;
    },
    memos() {
      const {
        source,
        description
      } = this.form;
      let sourceFrom = "";
      let descriptionUse = "";
      if (source) {
        sourceFrom = `From: ${source}\n`;
      }
      if (description) {
        descriptionUse = `Use: ${description}\n`;
      }
      return `${sourceFrom}${descriptionUse}`;
    },
    isSaveButtonDisabled() {
      return this.loading || this.saving || this.hasDateError || !this.form.date;
    },
    showTrustAccount() {
      if (!(this.trusts?.length > 1)) {
        return false;
      }
      const {
        client,
        matter
      } = this.form;
      // Requiring .billing rather than just the client waits out the lean option record the picker
      // assigns, so the field can't flash in before syncClient() reveals an existing trust account.
      if (!client?.billing) {
        return false;
      }
      if (client.billing.billClient) {
        return !client.billing.trust;
      }
      return !!matter && !matter.billing?.trust;
    }
  },
  methods: {
    /**
     * A parent that refreshes its own figures answers with preventDefault(), and we then
     * leave without re-entering its state -- it still re-reads from the server, only the
     * teardown is skipped. Anything that does not answer gets the reload, so a mount with
     * no listener can never be left showing a stale balance.
     */
    dismissAfterDeposit() {
      const handled = this.rootScope && this.rootScope.$broadcast("trust:depositRecorded").defaultPrevented;
      this.routeService.go("^", {}, handled ? {} : {
        reload: reloadParentOnly(this.routeService.currentStateName())
      });
    },
    async getTrust() {
      try {
        this.me = await this.userService.getMe();
        this.trusts = this.me.company.settings.trust?.accounts?.map(account => ({
          value: account.id,
          ...account
        }));
        // All four role flags off is the /options equivalent of the old scope:'all' load. Pairing
        // that with our own account id keeps restricted-access matters we're shared on in the list;
        // a null scopeAccount would silently drop every one of them.
        this.cmQuery = {
          originating: false,
          involved: false,
          reviewer: false,
          primary: false,
          scopeAccount: this.me.id,
          archived: false,
          showSecondarySplitMatter: true
        };
        this.routeService = new RouteService(this);
        const {
          id,
          clientId,
          matterId,
          client,
          matter,
          trustId
        } = this.routeService.params();

        // The `.client.matter.billing.deposit` / `.client.matter.settlement.deposit`
        // states don't declare a `clientId` param — the client id arrives as `id`,
        // inherited from the parent `.client` state's `/client/{id}` url.
        // `client` is an object param, so take its id: getClient() interpolates whatever
        // it's handed straight into `/clients/{id}`. Optional chaining because the states
        // that declare it default it to null.
        const c = clientId || id || client?.id;
        const m = matterId ? matterId : matter;
        // force: true throughout. The store caches one id at a time, and recording a deposit
        // changes billing.trust server-side -- a cached record would re-show the "accounts will
        // be created in QuickBooks" warning for a client that already has a trust account.
        if (m) this.form.matter = await this.clientMatterStore.getMatterById(m, true);
        if (c) this.form.client = await this.clientMatterStore.getClientById(c, true);else if (this.form.matter?.client?.id) {
          // Auto-derive client from matter when only matterId is in params (e.g. Settlement tab)
          // Keep pick=true so user can still change client/matter via dropdowns
          this.form.client = await this.clientMatterStore.getClientById(this.form.matter.client.id, true);
          this.pick = true;
        }
        if (!this.form.client || !this.form.matter && this.form.client.billing?.billClient) {
          this.pick = true;
        }
        this.form.trust = this.trusts?.find(t => trustId ? t.id === trustId : t.defaultAccount);
        if (!this.form.trust && this.me.company.settings.trust) {
          this.form.trust = this.trusts?.find(t => t.defaultAccount);
        }
        if (this.settlementContext) {
          this.pick = false;
          if (!this.form.description) this.form.description = 'Settlement Funds';
        }

        // Seed the picker with whatever the route resolved. Assigning the full records is safe:
        // the picker swaps in its own lean ones, and the id-keyed watchers see no change.
        this.cm.client = this.form.client || null;
        this.cm.matter = this.form.matter || null;
      } catch (err) {
        this.alerts.error(this.formatApiError(err));
      } finally {
        this.loading = false;
      }
    },
    /**
     * Swap the picker's lean client option for the full record: this component reads
     * billing.billClient, billing.trust and accounting off it, none of which /clients/options returns.
     */
    async syncClient(clientId) {
      if (!clientId) {
        this.form.client = null;
        this.updateTrustAccount();
        return;
      }
      if (this.form.client?.id === clientId) return;
      try {
        this.form.client = await this.clientMatterStore.getClientById(clientId, true);
      } catch (err) {
        // Only act if this is still the selection: a rejection for a client the user has already
        // moved off would otherwise wipe the good one they can see.
        if (this.cm.client?.id !== clientId) return;
        // Keeping the previous client here would let save() record the deposit against a client
        // the picker is no longer showing. Drop the selection instead, so save()'s own
        // "You have to select a client" check catches it.
        this.form.client = null;
        this.cm.client = null;
        this.alerts.error(this.formatApiError(err));
        return;
      }
      this.setDefaultClientBilling();
      if (this.form.client.billing?.billClient) {
        // setDefaultClientBilling() drops form.matter; clear the picker's copy to match.
        this.cm.matter = null;
      }
      this.updateTrustAccount();
    },
    /** Same swap for the matter: billing.trust and accounting are what save() needs. */
    async syncMatter(matterId) {
      // A client-level client has no matter on the deposit, but the picker can still auto-select
      // one behind its hidden matter field, so refuse it here rather than let it reach form.matter.
      if (!matterId || this.form.client?.billing?.billClient) {
        this.form.matter = null;
        this.updateTrustAccount();
        return;
      }
      if (this.form.matter?.id === matterId) return;
      try {
        this.form.matter = await this.clientMatterStore.getMatterById(matterId, true);
      } catch (err) {
        if (this.cm.matter?.id !== matterId) return;
        // Same divergence as syncClient: a stale form.matter would be what save() posts.
        this.form.matter = null;
        this.cm.matter = null;
        this.alerts.error(this.formatApiError(err));
        return;
      }
      this.updateTrustAccount();
    },
    // SC-45762: currency-formatted amount input handlers (logic extracted to utils.js)
    onAmountInput(e) {
      const el = e.target;
      const {
        formatted,
        newCursor
      } = formatCurrencyInputTyping(el.value, el.selectionEnd ?? el.value.length);
      el.value = formatted;
      this.displayAmount = formatted;
      el.setSelectionRange(newCursor, newCursor);
      this.form.amount = parseCurrencyInput(formatted) ?? undefined;
    },
    onAmountBlur() {
      this.displayAmount = formatCurrencyInputDisplay(this.form.amount);
    },
    onAmountFocus() {
      // Strip formatting on focus so the user can type freely without fighting commas.
      // Use the raw numeric value if available; otherwise clear so placeholder shows.
      this.displayAmount = this.form.amount != null && this.form.amount > 0 ? String(this.form.amount) : '';
    },
    async getAccountingMethods() {
      const {
        methods
      } = await this.leanLawApi.listAccountingMethods();
      this.accountingMethods = methods.map(method => ({
        value: {
          id: method.id,
          name: method.name,
          type: method.type
        },
        ...method
      }));
    },
    cancel() {
      if (this.onCancel) {
        this.onCancel();
      } else {
        this.routeService.go("^");
      }
    },
    setDefaultClientBilling() {
      if (!this.form.client.accounting) {
        // set default for billClient so that downstream logic can create things correctly
        this.form.client.billing = this.form.client?.billing || {};
        if (typeof this.form.client.billing.billClient === "undefined") {
          this.form.client.billing.billClient = this.me.company.settings.clientBilling.billClient;
        }
        if (this.form.client.billing?.billClient) {
          this.form.matter = null;
        }
      }
    },
    alertMsg(msg) {
      this.saving = false;
      this.alerts.warning(msg);
    },
    formatApiError(err) {
      // Extract message and detail from error object
      let message;
      let detail;
      if (typeof err === 'string') {
        message = err;
      } else if (err?.body) {
        if (typeof err.body === 'string') {
          message = err.body;
        } else {
          message = err.body.message;
          detail = err.body.detail;
        }
      }
      // Fallback chain: message → detail → err.message → generic
      message = message || detail || err?.message || 'An unexpected error occurred. Please try again.';

      // Check both message and detail for QuickBooks deleted/disabled account info
      const fullText = [message, detail].filter(Boolean).join(' ');
      if (/Account assigned to this transaction has been deleted/i.test(fullText)) {
        const accountMatch = fullText.match(/you must restore\s+(.+?)(?:\.\.|$)/i);
        const accountName = accountMatch?.[1]?.trim();
        if (accountName) {
          return `The QuickBooks account "${accountName}" has been deleted or disabled. Please restore it in QuickBooks Online before recording this deposit.`;
        }
        return 'A QuickBooks account linked to this transaction has been deleted or disabled in QuickBooks Online. Please restore it in QuickBooks before recording this deposit.';
      }
      return message;
    },
    save() {
      this.saving = true;
      const {
        amount,
        date,
        client,
        source,
        method,
        matter,
        trust
      } = this.form;
      if (this.hasDateError === true) {
        this.saving = false;
        return this.alertMsg('Date is invalid');
      }
      if (!client) {
        this.saving = false;
        return this.alertMsg("You have to select a client to record a deposit");
      }
      if (!(amount > 0)) {
        this.saving = false;
        return this.alertMsg("An amount is required to record a deposit");
      }
      if (!date) {
        this.saving = false;
        return this.alertMsg("Date is required to record a deposit");
      }
      if (!method) {
        this.saving = false;
        return this.alertMsg("Payment Method is required to record a deposit");
      }
      if (!source) {
        this.saving = false;
        return this.alertMsg("The 'From' field is required");
      }
      this.ensureConnected().then(() => {
        const {
          billClient
        } = this.form.client.billing;
        if (!this.form.matter && !billClient) {
          this.saving = false;
          return this.alertMsg(`You have to select a ${this.me.terms.matter} to record a deposit`);
        }
        const description = this.memos;
        const request = {
          id: billClient ? client.id : matter.id,
          body: {
            amount,
            date: date.toNetworkDate(),
            description,
            trust: trust.id && {
              id: trust.id
            },
            method: {
              id: method.id,
              name: method.name,
              type: method.type
            }
          }
        };
        const api = billClient ? this.leanLawApi.createClientDeposit(request) : this.leanLawApi.createMatterDeposit(request);
        api.then(() => {
          this.alerts.success("The deposit was recorded in the client trust account");
          if (this.onSaved) {
            this.onSaved();
          } else {
            this.dismissAfterDeposit();
          }
        }).catch(err => {
          return this.alertMsg(this.formatApiError(err));
        }).finally(() => {
          this.saving = false;
        });
      }).catch(err => {
        this.saving = false;
        return this.alertMsg(this.formatApiError(err));
      });
    },
    ensureConnected() {
      const {
        billClient
      } = this.form.client.billing;
      if ((!this.form.matter || this.form.matter.accounting || billClient) && this.form.client.accounting) {
        return new Promise(resolve => resolve());
      }

      // Not linked to a QuickBooks customer yet -- sort it out. No re-fetch first: form.client
      // and form.matter already hold freshly-loaded records (getClientById/getMatterById run
      // with force), where the old path re-read them purely to obtain a model instance.
      if (this.form.matter) {
        return getOrConnectMatter(this.form.matter, this.me).then(accounting => {
          this.connect(accounting, this.me.terms.matter, {
            matterId: this.form.matter.id
          });
        });
      }
      return getOrConnectClient(this.form.client, this.me).then(accounting => {
        this.connect(accounting, "client", {
          clientId: this.form.client.id
        });
      });
    },
    connect(accounting, item, state) {
      if (accounting.connect) {
        if (this.onCancel || this.onSaved) {
          // Modal context: cannot navigate to .connect child route — show an error and
          // throw so that ensureConnected()'s promise chain rejects and save() is halted.
          const msg = `To record this deposit, the ${item} must first be connected to a QuickBooks customer. Please do this from the ${item} settings.`;
          this.alertMsg(msg);
          throw new Error(msg);
        }
        state.message = `To setup trust accounting, the ${item} must be associated with a QuickBooks customer`;
        state.onCancel = function () {
          this.message = `The ${item} must be connected to a QuickBooks customer`;
        };
        this.routeService.go(".connect", state);
      }
    },
    handleDateError(hasError) {
      this.hasDateError = hasError;
    },
    updateTrustAccount() {
      if (this.form.client?.billing?.billClient) {
        this.form.trust = this.trusts?.find(t => t.id === this.form.client.billing.trust?.id);
      } else if (this.form.matter?.billing?.trust) {
        this.form.trust = this.trusts.find(t => t.id === this.form.matter.billing.trust?.id);
      } else {
        // Fallback: Use the default trust account if no trust is associated with the matter
        this.form.trust = this.trusts?.find(t => t.defaultAccount);
      }
      if (!this.form.trust && this.me.company.settings.trust) {
        this.form.trust = this.trusts?.find(t => t.defaultAccount);
      }
    }
  }
};